import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, KeyRound, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { adminUsers, adminRoles, crud, setUserPassword } from "@/lib/apiData";
import { useAuth } from "@/context/AuthContext";

const PERM_LABELS = { overview: "Overview", orders: "Orders", catalog: "Food & chefs", content: "Blog & pages", website: "Website", settings: "Settings", users: "Users & roles", data: "Data & backup", audit: "Audit log" };
const api = crud("users");
const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");
const fieldCls = "mt-1 h-11 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]";

const RoleMatrix = ({ roles }) => (
  <div className="bg-white border border-neutral-200 overflow-x-auto mb-8" data-testid="role-matrix">
    <Table>
      <TableHeader><TableRow><TableHead>Access</TableHead>{roles.map((r) => <TableHead key={r.id} className="text-center">{r.label}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {Object.entries(PERM_LABELS).map(([k, label]) => (
          <TableRow key={k}>
            <TableCell className="text-neutral-700">{label}</TableCell>
            {roles.map((r) => <TableCell key={r.id} className="text-center">{r.perms.includes(k) ? <Check className="w-4 h-4 text-[#d00000] inline" /> : <span className="text-neutral-300">—</span>}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const NewUserDialog = ({ open, onClose, roles, onDone }) => {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "editor" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.create(f);
      toast.success("Team member added");
      setF({ name: "", email: "", password: "", role: "editor" });
      onDone();
      onClose();
    } catch (x) { err(x); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="new-user-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add team member</DialogTitle><DialogDescription>They can sign in right away with this password.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" className={fieldCls} />
          <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" className={fieldCls} />
          <Input required type="password" autoComplete="new-password" placeholder="Temporary password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password" className={fieldCls} />
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="new-user-role" className="w-full h-11 px-3 bg-[#fbf9f7] border border-neutral-300">
            {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <DialogFooter><Button type="submit" data-testid="new-user-submit" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">Add member</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PasswordDialog = ({ target, onClose }) => {
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await setUserPassword(target.id, pw);
      toast.success(`Password updated for ${target.email}`);
      setPw("");
      onClose();
    } catch (x) { err(x); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="set-password-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Set new password</DialogTitle><DialogDescription>{target?.email}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Input required type="password" autoComplete="new-password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="set-password-input" className={fieldCls} />
          <DialogFooter><Button type="submit" data-testid="set-password-submit" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">Update password</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminUsers() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [] } = useQuery({ queryKey: ["admin-users"], queryFn: adminUsers });
  const { data: roles = [] } = useQuery({ queryKey: ["admin-roles"], queryFn: adminRoles });
  const [adding, setAdding] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [filter, setFilter] = useState("team");
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const update = (u, payload) => api.update(u.id, payload).then(() => { toast.success("Saved"); refresh(); }).catch(err);
  const remove = (u) => window.confirm(`Delete ${u.email}? This cannot be undone.`) && api.remove(u.id).then(() => { toast.success("Deleted"); refresh(); }).catch(err);
  const roleIds = roles.map((r) => r.id);
  const shown = users.filter((u) => (filter === "team" ? roleIds.includes(u.role) : !roleIds.includes(u.role)));

  return (
    <div data-testid="admin-users-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl font-black text-neutral-900">Users & roles</h2>
          <p className="text-neutral-500 text-sm mt-1">Add team members, choose what they can access and manage passwords.</p>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="add-user-btn" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2"><Plus className="w-4 h-4" /> Add team member</Button>
      </div>
      <RoleMatrix roles={roles} />
      <div className="flex gap-2 mb-4">
        {[["team", "Team"], ["customers", "Customers"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`users-filter-${k}`} className={`px-4 h-9 text-sm border ${filter === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-300 text-neutral-700"}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {shown.map((u) => {
              const self = u.id === me?.id;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-neutral-900">{u.name}{self && <span className="ml-2 text-xs text-neutral-400">(you)</span>}</TableCell>
                  <TableCell className="text-neutral-600">{u.email}</TableCell>
                  <TableCell>
                    <select disabled={self} value={u.role} onChange={(e) => update(u, { role: e.target.value })} data-testid={`user-role-${u.email}`} className="h-9 px-2 bg-[#fbf9f7] border border-neutral-300 text-sm disabled:opacity-60">
                      {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      <option value="user">Customer</option>
                    </select>
                  </TableCell>
                  <TableCell><Switch disabled={self} checked={u.active !== false} onCheckedChange={(v) => update(u, { active: v })} data-testid={`user-active-${u.email}`} className="data-[state=checked]:bg-[#d00000]" /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => setPwFor(u)} data-testid={`user-password-${u.email}`} className="inline-flex w-8 h-8 items-center justify-center text-neutral-500 hover:text-[#d00000]" aria-label="Set password"><KeyRound className="w-4 h-4" /></button>
                    {!self && <button onClick={() => remove(u)} data-testid={`user-delete-${u.email}`} className="inline-flex w-8 h-8 items-center justify-center text-neutral-500 hover:text-[#d00000]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {shown.length === 0 && <p className="p-8 text-center text-neutral-400">No users here yet.</p>}
      </div>
      <NewUserDialog open={adding} onClose={() => setAdding(false)} roles={roles} onDone={refresh} />
      <PasswordDialog target={pwFor} onClose={() => setPwFor(null)} />
    </div>
  );
}
